import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { initTracking, trackPageView, trackCtaAudit } from "./tracking";

function getScoreColor(s) {
  if (s < 40) return "#EF4444";
  if (s < 70) return "#F59E0B";
  return "#10B981";
}

function getTitleScore(title) {
  const len = title.length;
  if (len < 20) return Math.round(10 + len * 0.7);
  if (len < 40) return Math.round(24 + (len - 20) * 1.2);
  if (len < 80) return Math.round(48 + (len - 40) * 0.7);
  if (len < 150) return Math.round(76 + (len - 80) * 0.25);
  return 93;
}

const DEMO_DESCRIPTIONS = [
  { before: "Comfortable cotton t-shirt.", after: "Premium 100% organic cotton black t-shirt for men & women. Breathable, pre-shrunk fabric with reinforced double-stitched seams for lasting durability. Available in sizes XS to 3XL. Perfect for everyday casual wear, layering, or as a wardrobe staple. Machine washable at 30\u00b0C." },
  { before: "Great for running.", after: "Lightweight performance running shoes with responsive cushioning, breathable mesh upper, and durable rubber outsole for all-terrain grip. Designed for neutral to mild pronation runners. Reflective details for low-light visibility. Weighs only 245g per shoe." },
  { before: "Brown leather wallet for men.", after: "Genuine full-grain leather bifold wallet with RFID-blocking technology. Features 8 card slots, 2 bill compartments, and 1 ID window. Slim profile fits comfortably in front or back pocket. Hand-stitched edges with contrast thread for a premium finish." },
  { before: "", after: "Non-slip eco-friendly TPE yoga mat, 6mm thick. Extra-wide 72x26 inch design with alignment marks for proper posture. Double-layered for superior cushioning and joint support. Includes carrying strap. Free from PVC, latex, and toxic chemicals." },
  { before: "Protects your phone.", after: "Military-grade shockproof clear case for iPhone 15 with MagSafe compatibility. 12ft drop-tested with air-cushion corners. Anti-yellowing polycarbonate back with scratch-resistant coating. Raised bezels protect camera and screen. Slim 1.2mm profile." },
];

const DEMO_HIGHLIGHTS = [
  { before: null, after: ["Material: 100% organic cotton", "Fit: Unisex, relaxed fit", "Care: Machine washable at 30\u00b0C"] },
  { before: null, after: ["Cushioning: Responsive foam midsole", "Upper: Breathable mesh, lightweight", "Sole: Durable rubber, all-terrain grip"] },
  { before: ["Material: Leather"], after: ["Material: Full-grain genuine leather", "Security: RFID-blocking technology", "Capacity: 8 cards, 2 bill compartments"] },
  { before: null, after: ["Material: Eco-friendly TPE, non-toxic", "Size: 72\u00d726 inches, extra wide", "Thickness: 6mm premium cushioning"] },
  { before: null, after: ["Protection: 12ft military-grade drop test", "Compatibility: MagSafe certified", "Material: Anti-yellowing polycarbonate"] },
];

const DEMO_DATA = {
  globalScore: 32,
  titleScore: 24,
  descriptionScore: 18,
  highlightsScore: 8,
  totalProducts: 214,
  analyzedProducts: 50,
  storeName: "test.com",
  avgTitleLen: 28,
  avgDescLen: 95,
  hlMissingPct: 87,
  criticalIssues: [
    "Product titles are too short (avg 28 chars, optimal is 70\u2013150)",
    "Descriptions lack detail (avg 95 chars, optimal is 500\u20135,000)",
    "Product Highlights are missing or not configured",
  ],
  beforeAfter: [
    { before: "Black T-Shirt", after: "Premium Cotton Black T-Shirt \u2014 Breathable, Unisex, 2026" },
    { before: "Running Shoes", after: "Lightweight Running Shoes for Men \u2014 Cushioned, All-Terrain, Durable" },
    { before: "Leather Wallet", after: "Genuine Leather Bifold Wallet \u2014 RFID Blocking, Slim Design, Brown" },
    { before: "Yoga Mat", after: "Non-Slip Yoga Mat 6mm \u2014 Eco-Friendly TPE, Extra Wide, Purple" },
    { before: "Phone Case", after: "Shockproof iPhone 15 Case \u2014 Military-Grade, Clear, MagSafe Compatible" },
  ],
  descriptions: DEMO_DESCRIPTIONS,
  highlights: DEMO_HIGHLIGHTS,
};

function ScorePill({ score }) {
  const color = getScoreColor(score);
  const bg = score < 40 ? "#FEE2E2" : score < 70 ? "#FEF3C7" : "#D1FAE5";
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color, background: bg,
      padding: "3px 8px", borderRadius: 20, whiteSpace: "nowrap",
    }}>
      {score}/100
    </span>
  );
}

function MagicFeedLogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
        <defs>
          <linearGradient id="mfgL" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6B8BF5"/>
            <stop offset="100%" stopColor="#4B6AE8"/>
          </linearGradient>
        </defs>
        <path d="M18 4L20.5 14L31 16.5L20.5 19L18 29L15.5 19L5 16.5L15.5 14Z" fill="url(#mfgL)"/>
        <path d="M34 6L35.2 10L39 11.2L35.2 12.4L34 16L32.8 12.4L29 11.2L32.8 10Z" fill="url(#mfgL)" opacity="0.7"/>
        <circle cx="40" cy="22" r="2" fill="url(#mfgL)" opacity="0.5"/>
      </svg>
      <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 22, lineHeight: 1, letterSpacing: -0.3 }}>
        <span style={{ fontWeight: 800, color: "#0f172a" }}>Magic</span>
        <span style={{ fontWeight: 400, color: "#0f172a" }}>Feed</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#4B6AE8", marginLeft: 2 }}>Pro</span>
      </span>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }, [text]);

  return (
    <button onClick={handleCopy} title="Copy" style={{
      background: copied ? "#D1FAE5" : "#F3F4F6",
      border: `1.5px solid ${copied ? "#6EE7B7" : "#D1D5DB"}`,
      borderRadius: 6, padding: "5px 9px", cursor: "pointer",
      lineHeight: 1, fontFamily: "'Inter',sans-serif", transition: "all 0.15s ease",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      )}
    </button>
  );
}

function ExpandModal({ open, onClose, label, productName, fullText }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, padding: 32,
        maxWidth: 560, width: "100%", position: "relative",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        animation: "popIn 0.2s ease",
        fontFamily: "'Inter',sans-serif",
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16,
          background: "none", border: "none", cursor: "pointer",
          fontSize: 18, color: "#94a3b8", padding: 4, lineHeight: 1,
        }}>✕</button>

        {productName && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>{productName}</div>
        )}
        <div style={{ fontSize: 13, fontWeight: 700, color: "#4361EE", marginBottom: 16 }}>
          ✦ AI Optimized {label}
        </div>
        <div style={{
          fontSize: 16, color: "#1a1a2e", lineHeight: 1.8,
          marginBottom: 24, whiteSpace: "pre-wrap",
        }}>
          {fullText}
        </div>
        <button onClick={handleCopy} style={{
          width: "100%", padding: "12px 16px",
          background: copied ? "#D1FAE5" : "#4361EE",
          border: "none", borderRadius: 10, cursor: "pointer",
          color: copied ? "#059669" : "#fff",
          fontSize: 14, fontWeight: 600,
          fontFamily: "'Inter',sans-serif",
          transition: "background 0.2s ease, color 0.2s ease",
        }}>
          {copied ? "✓ Copied!" : "Copy to clipboard"}
        </button>
      </div>
    </div>
  );
}

function RowActions({ text, productName, label }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <span className="audit-row-actions" style={{
        display: "inline-flex", gap: 4, marginLeft: 6, flexShrink: 0, verticalAlign: "middle",
      }}>
        <button onClick={(e) => { e.stopPropagation(); setShowModal(true); }} title="Expand" style={{
          background: "#F3F4F6", border: "1.5px solid #D1D5DB",
          borderRadius: 6, padding: "5px 9px", cursor: "pointer",
          lineHeight: 1, fontFamily: "'Inter',sans-serif",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/></svg>
        </button>
        <CopyButton text={text} />
      </span>
      <ExpandModal
        open={showModal}
        onClose={() => setShowModal(false)}
        label={label}
        productName={productName}
        fullText={text}
      />
    </>
  );
}

export default function AuditPage() {
  const [searchParams] = useSearchParams();
  const [auditData, setAuditData] = useState(null);
  const [displayScore, setDisplayScore] = useState(0);
  const [activeTab, setActiveTab] = useState("titles");

  useEffect(() => {
    initTracking();
    trackPageView("audit");

    async function loadData() {
      let data = null;
      if (searchParams.get("demo") === "true") {
        data = DEMO_DATA;
      } else if (searchParams.get("rid")) {
        // Remote KV audit (from email link)
        try {
          const res = await fetch(`/api/audit-store?id=${searchParams.get("rid")}`);
          if (res.ok) { const json = await res.json(); data = json.data; }
        } catch {}
      } else if (searchParams.get("id")) {
        // Local storage audit (from admin)
        try {
          const raw = localStorage.getItem(searchParams.get("id"));
          if (raw) data = JSON.parse(raw);
        } catch {}
      } else {
        try {
          const raw = sessionStorage.getItem("mfp_audit");
          if (raw) data = JSON.parse(raw);
        } catch {}
      }
      return data;
    }

    loadData().then(data => {
    if (data) {
      // Ensure descriptions/highlights data exists
      if (!data.descriptions) data.descriptions = DEMO_DESCRIPTIONS;
      if (!data.highlights) data.highlights = DEMO_HIGHLIGHTS;
      if (!data.avgTitleLen) data.avgTitleLen = 28;
      if (!data.avgDescLen) data.avgDescLen = 95;
      if (!data.hlMissingPct) data.hlMissingPct = 87;

      setAuditData(data);
      const target = data.globalScore;
      const start = Date.now();
      const tick = () => {
        const p = Math.min((Date.now() - start) / 1000, 1);
        setDisplayScore(Math.round(p * target));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    });
  }, [searchParams]);

  // No data fallback
  if (!auditData) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          html, body, #root { height:100%; overflow:auto; }
        `}</style>
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Inter',sans-serif",
          background: "linear-gradient(135deg,#f0f4ff 0%,#e8eeff 50%,#dde5ff 100%)",
          padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: "48px 40px", maxWidth: 480, width: "100%",
            textAlign: "center", boxShadow: "0 12px 60px rgba(75,106,232,0.12)",
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0D0D1A", marginBottom: 8 }}>No audit data found</h2>
            <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 24, lineHeight: 1.6 }}>
              It looks like you haven't run an audit yet. Start by scanning your store to generate your feed report.
            </p>
            <Link to="/" style={{
              display: "inline-block", padding: "12px 32px",
              background: "linear-gradient(135deg,#4B6AE8,#6B8BF5)",
              borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 600, textDecoration: "none",
              boxShadow: "0 4px 16px rgba(75,106,232,0.35)",
            }}>
              → Run Your Free Audit
            </Link>
          </div>
        </div>
      </>
    );
  }

  const scoreColor = getScoreColor(displayScore);
  const bgColor = displayScore < 40 ? "#FEF2F2" : displayScore < 70 ? "#FFFBEB" : "#F0FDF4";
  const borderColorScore = displayScore < 40 ? "#FECACA" : displayScore < 70 ? "#FDE68A" : "#BBF7D0";
  const headline = displayScore < 40 ? "Critical issues detected on your feed."
    : displayScore < 70 ? "Your feed is underperforming."
    : "Good feed — but you're leaving money on the table.";

  const analyzed = auditData.analyzedProducts || 50;
  const total = auditData.totalProducts || null;
  const remaining = total ? total - analyzed : null;
  const storeName = auditData.storeName || null;
  const industryAvg = 74;
  const gap = industryAvg - auditData.globalScore;
  const impImpressions = auditData.globalScore < 40 ? 67 : 43;
  const impClicks = auditData.globalScore < 40 ? 43 : 28;
  const impRevenue = auditData.globalScore < 40 ? 38 : 22;

  const TABS = [
    { key: "titles", label: "Titles" },
    { key: "descriptions", label: "Descriptions" },
    { key: "highlights", label: "Highlights" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { height:100%; overflow:auto; }
        @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
        @keyframes floatMF { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }

        .audit-ba-row .audit-row-actions { opacity: 0; transition: opacity 0.15s ease; }
        .audit-ba-row:hover .audit-row-actions { opacity: 1; }

        @media (max-width: 640px) {
          .audit-main-card { padding: 20px 16px !important; border-radius: 18px !important; }
          .audit-score-gauge { padding: 14px 16px !important; gap: 12px !important; }
          .audit-subscores { flex-direction: column !important; }
          .audit-ba-row { flex-direction: column !important; gap: 6px !important; padding: 14px 12px !important; }
          .audit-ba-header { display: none !important; }
          .audit-ba-cell { flex: none !important; width: 100% !important; }
          .audit-ba-cell::before { content: attr(data-label); display: block; font-size: 10px; font-weight: 700; color: #6B7280; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
          .audit-ba-cell[data-label="✦ AI Optimized"] { flex-wrap: wrap !important; }
          .audit-row-actions { margin-left: 0 !important; margin-top: 6px !important; }
          .audit-ba-score { text-align: left !important; margin-top: 4px !important; }
          .audit-priority-row { flex-direction: column !important; }
          .audit-impact-row { flex-direction: column !important; }
          .audit-benchmark-cols { flex-direction: column !important; gap: 12px !important; }
          .audit-cta-section { padding: 24px 16px !important; border-radius: 12px !important; }
          .audit-cta-section h3 { font-size: 18px !important; }
          .audit-cta-section p { font-size: 13px !important; }
          .audit-cta-btn { padding: 14px 20px !important; font-size: 14px !important; max-width: 100% !important; }
          .audit-header { flex-direction: column !important; gap: 8px !important; align-items: flex-start !important; }
          .audit-ba-row .audit-row-actions { opacity: 1 !important; }
        }
      `}</style>

      <div style={{
        minHeight: "100vh", fontFamily: "'Inter',sans-serif",
        background: "linear-gradient(135deg,#f0f4ff 0%,#e8eeff 50%,#dde5ff 100%)",
        padding: "40px 20px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(75,106,232,0.08) 0%,transparent 70%)", top: "-10%", left: "-5%", pointerEvents: "none" }}/>
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(139,92,246,0.06) 0%,transparent 70%)", bottom: "-15%", right: "-5%", pointerEvents: "none" }}/>

        {[[8,12,14,0],[92,75,13,2],[5,80,11,1.5]].map(([x,y,s,d],i) => (
          <svg key={i} width={s} height={s} viewBox="0 0 16 16" fill="none" style={{ position: "absolute", left: `${x}%`, top: `${y}%`, animation: `floatMF 4s ease-in-out ${d}s infinite`, zIndex: 0 }}>
            <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#a5b4fc" opacity="0.45"/>
          </svg>
        ))}

        <div style={{ maxWidth: 672, margin: "0 auto", position: "relative", zIndex: 1 }}>

          {/* ── HEADER ── */}
          <div className="audit-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
            <MagicFeedLogo />
            <span style={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>Your Feed Audit Report</span>
          </div>

          {/* ── MAIN CARD ── */}
          <div style={{
            background: "linear-gradient(180deg, #f8faff 0%, #f0f4ff 100%)",
            borderRadius: 28, overflow: "hidden",
            boxShadow: "0 12px 60px rgba(75,106,232,0.12), 0 2px 8px rgba(0,0,0,0.04)",
            padding: "32px 36px",
            animation: "popIn 0.4s ease",
          }} className="audit-main-card">

            {/* Store info */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                {storeName && (
                  <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 2 }}>
                    Audit for <strong style={{ color: "#0D0D1A" }}>{storeName}</strong>
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#94a3b8" }}>
                  {analyzed} products analyzed{total ? ` out of ${total}` : ""}
                </div>
              </div>
              <div style={{
                background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 20,
                padding: "4px 12px", fontSize: 11, fontWeight: 600, color: "#4361EE",
              }}>
                Free audit
              </div>
            </div>

            {/* ── SCORE GAUGE ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 16,
              background: bgColor, borderRadius: 14, padding: "18px 22px",
              border: `1px solid ${borderColorScore}`, marginBottom: 24,
            }} className="audit-score-gauge">
              <div style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                <svg width="72" height="72" viewBox="0 0 72 72" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="36" cy="36" r="30" fill="none" stroke={borderColorScore} strokeWidth="5"/>
                  <circle cx="36" cy="36" r="30" fill="none" stroke={scoreColor} strokeWidth="5"
                    strokeDasharray={`${(displayScore / 100) * 188.5} 188.5`}
                    strokeLinecap="round" style={{ transition: "stroke-dasharray 0.1s linear" }}/>
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{displayScore}</span>
                  <span style={{ fontSize: 8, color: "#94a3b8" }}>/100</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#0D0D1A", marginBottom: 2 }}>Feed Quality Score</div>
                <div style={{ fontSize: 13, color: scoreColor, fontWeight: 600 }}>{headline}</div>
              </div>
            </div>

            {/* ── SUB-SCORES ROW ── */}
            <div className="audit-subscores" style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {[
                { icon: "📝", label: "Title Score", score: auditData.titleScore },
                { icon: "📄", label: "Description", score: auditData.descriptionScore },
                { icon: "⭐", label: "Highlights", score: auditData.highlightsScore },
              ].map((s, i) => (
                <div key={i} style={{
                  flex: 1, background: "#fff", borderRadius: 12, padding: "16px 12px",
                  border: "1px solid #E5E7EB", textAlign: "center",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6, fontWeight: 500 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: getScoreColor(s.score) }}>{s.score}<span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8" }}>/100</span></div>
                </div>
              ))}
            </div>

            {/* ══════════════════════════════════════════════════
                ADDITION 1 — COMPETITIVE BENCHMARK
               ══════════════════════════════════════════════════ */}
            <div style={{
              background: "#fff", borderRadius: 12, padding: "18px 20px",
              borderLeft: "3px solid #F59E0B", border: "1px solid #E5E7EB",
              borderLeftWidth: 3, borderLeftColor: "#F59E0B",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 24,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0D0D1A", marginBottom: 14 }}>
                📊 How you compare to competitors
              </div>
              <div className="audit-benchmark-cols" style={{ display: "flex", gap: 24, marginBottom: 14 }}>
                <div style={{ flex: 1, textAlign: "center", padding: "12px 0", background: bgColor, borderRadius: 10, border: `1px solid ${borderColorScore}` }}>
                  <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4, fontWeight: 500 }}>Your score</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor }}>{auditData.globalScore}<span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8" }}>/100</span></div>
                </div>
                <div style={{ flex: 1, textAlign: "center", padding: "12px 0", background: "#F0FDF4", borderRadius: 10, border: "1px solid #BBF7D0" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4, fontWeight: 500 }}>Industry average</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#10B981" }}>74<span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8" }}>/100</span></div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>
                You are <strong style={{ color: "#EF4444" }}>{gap} points behind</strong> the average Shopify store on Google Shopping.
              </div>
            </div>

            {/* ── CRITICAL ISSUES ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0D0D1A", marginBottom: 10 }}>Critical Issues</div>
              {auditData.criticalIssues.map((issue, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", marginBottom: 6, borderRadius: 10,
                  background: "rgba(254,226,226,0.5)", border: "1px solid rgba(239,68,68,0.12)",
                }}>
                  <span style={{ fontSize: 14, color: "#EF4444", flexShrink: 0, fontWeight: 700 }}>⚠</span>
                  <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.4 }}>{issue}</span>
                </div>
              ))}
            </div>

            {/* ══════════════════════════════════════════════════
                ADDITION 2 — IMPACT ESTIMATE
               ══════════════════════════════════════════════════ */}
            <div style={{
              background: "#fff", borderRadius: 12, padding: "18px 20px",
              borderLeft: "3px solid #4361EE", border: "1px solid #E5E7EB",
              borderLeftWidth: 3, borderLeftColor: "#4361EE",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 24,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0D0D1A", marginBottom: 14 }}>
                📈 Estimated impact if you fix these issues
              </div>
              <div className="audit-impact-row" style={{ display: "flex", gap: 8 }}>
                {[
                  { value: `+${impImpressions}%`, label: "More impressions" },
                  { value: `+${impClicks}%`, label: "More clicks" },
                  { value: `+${impRevenue}%`, label: "More revenue" },
                ].map((stat, i) => (
                  <div key={i} style={{
                    flex: 1, textAlign: "center", padding: "14px 8px",
                    background: "#F0FDF4", borderRadius: 10, border: "1px solid #BBF7D0",
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#10B981", marginBottom: 2 }}>{stat.value}</div>
                    <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                Based on average results across 200+ optimized stores.
              </div>
            </div>

            {/* ══════════════════════════════════════════════════
                ADDITION 3 — TABBED BEFORE/AFTER TABLE
               ══════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0D0D1A" }}>Before / After AI Optimization</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>Sample of {auditData.beforeAfter.length} products</div>
              </div>

              {/* Tab navigation */}
              <div style={{ display: "flex", gap: 0, marginBottom: 0, borderBottom: "2px solid #E5E7EB" }}>
                {TABS.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "10px 20px", fontSize: 13, fontWeight: 600,
                    fontFamily: "'Inter',sans-serif",
                    color: activeTab === tab.key ? "#4361EE" : "#6B7280",
                    borderBottom: activeTab === tab.key ? "2.5px solid #4361EE" : "2.5px solid transparent",
                    marginBottom: -2, transition: "color 0.2s, border-color 0.2s",
                  }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={{
                background: "#fff", borderRadius: "0 0 12px 12px", border: "1px solid #E5E7EB", borderTop: "none",
                overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}>

                {/* ── TITLES TAB ── */}
                {activeTab === "titles" && (
                  <>
                    <div className="audit-ba-header" style={{ display: "flex", background: "#F3F4F6", padding: "10px 16px", gap: 12 }}>
                      <span style={{ flex: 2, fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Current Title</span>
                      <span style={{ flex: 3, fontSize: 11, fontWeight: 700, color: "#4361EE", textTransform: "uppercase", letterSpacing: 0.5 }}>✦ AI Optimized</span>
                      <span style={{ flex: 0, minWidth: 56, fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "right" }}>Score</span>
                    </div>
                    {auditData.beforeAfter.map((row, i) => {
                      const titleSc = row.titleScore != null ? row.titleScore : getTitleScore(row.before);
                      return (
                        <div className="audit-ba-row" key={i} style={{
                          display: "flex", alignItems: "center", padding: "12px 16px", gap: 12,
                          borderTop: "1px solid #F3F4F6",
                          background: i % 2 === 0 ? "#fff" : "#FAFBFC",
                        }}>
                          <span className="audit-ba-cell" data-label="Current Title" style={{ flex: 2, fontSize: 12, color: "#9CA3AF", lineHeight: 1.5 }}>{row.before}</span>
                          <span className="audit-ba-cell" data-label="✦ AI Optimized" style={{ flex: 3, fontSize: 12, color: "#374151", fontWeight: 600, lineHeight: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ flex: "1 1 100%" }}>{row.after}</span>
                            <RowActions text={row.after} productName={row.before} label="Title" />
                          </span>
                          <span className="audit-ba-score" style={{ flex: 0, minWidth: 56, textAlign: "right" }}><ScorePill score={titleSc} /></span>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── DESCRIPTIONS TAB ── */}
                {activeTab === "descriptions" && (
                  <>
                    <div className="audit-ba-header" style={{ display: "flex", background: "#F3F4F6", padding: "10px 16px", gap: 12 }}>
                      <span style={{ flex: 2, fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Current Description</span>
                      <span style={{ flex: 3, fontSize: 11, fontWeight: 700, color: "#4361EE", textTransform: "uppercase", letterSpacing: 0.5 }}>✦ AI Optimized</span>
                      <span style={{ flex: 0, minWidth: 56, fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "right" }}>Score</span>
                    </div>
                    {auditData.descriptions.map((row, i) => {
                      const descSc = row.before ? Math.min(55, Math.round(10 + row.before.length * 0.5)) : 5;
                      const productName = auditData.beforeAfter[i]?.before || `Product ${i + 1}`;
                      return (
                        <div className="audit-ba-row" key={i} style={{
                          display: "flex", alignItems: "center", padding: "12px 16px", gap: 12,
                          borderTop: "1px solid #F3F4F6",
                          background: i % 2 === 0 ? "#fff" : "#FAFBFC",
                        }}>
                          <span className="audit-ba-cell" data-label="Current" style={{ flex: 2, fontSize: 12, lineHeight: 1.5, color: row.before ? "#9CA3AF" : "#EF4444", fontStyle: row.before ? "normal" : "italic" }}>
                            {row.before || "No description"}
                          </span>
                          <span className="audit-ba-cell" data-label="✦ AI Optimized" style={{ flex: 3, fontSize: 12, color: "#374151", fontWeight: 600, lineHeight: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ flex: "1 1 100%" }}>{row.after}</span>
                            <RowActions text={row.after} productName={productName} label="Description" />
                          </span>
                          <span className="audit-ba-score" style={{ flex: 0, minWidth: 56, textAlign: "right" }}><ScorePill score={descSc} /></span>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── HIGHLIGHTS TAB ── */}
                {activeTab === "highlights" && (
                  <>
                    <div className="audit-ba-header" style={{ display: "flex", background: "#F3F4F6", padding: "10px 16px", gap: 12 }}>
                      <span style={{ flex: 2, fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Current Highlights</span>
                      <span style={{ flex: 3, fontSize: 11, fontWeight: 700, color: "#4361EE", textTransform: "uppercase", letterSpacing: 0.5 }}>✦ AI Optimized</span>
                    </div>
                    {auditData.highlights.map((row, i) => {
                      const productName = auditData.beforeAfter[i]?.before || `Product ${i + 1}`;
                      const fullText = row.after.map(h => `✓ ${h}`).join("\n");
                      return (
                        <div className="audit-ba-row" key={i} style={{
                          display: "flex", padding: "12px 16px", gap: 12,
                          borderTop: "1px solid #F3F4F6",
                          background: i % 2 === 0 ? "#fff" : "#FAFBFC",
                        }}>
                          <span className="audit-ba-cell" data-label="Current" style={{ flex: 2, fontSize: 12, lineHeight: 1.8 }}>
                            {row.before ? (
                              row.before.map((h, j) => (
                                <span key={j} style={{ display: "block", color: "#9CA3AF" }}>• {h}</span>
                              ))
                            ) : (
                              <span style={{ color: "#EF4444", fontWeight: 600 }}>✗ Not configured</span>
                            )}
                          </span>
                          <span className="audit-ba-cell" data-label="✦ AI Optimized" style={{ flex: 3, fontSize: 12, lineHeight: 1.8, display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>
                            <span style={{ flex: "1 1 100%" }}>
                              {row.after.map((h, j) => (
                                <span key={j} style={{ display: "block", color: "#059669", fontWeight: 500 }}>✓ {h}</span>
                              ))}
                            </span>
                            <RowActions text={fullText} productName={productName} label="Highlights" />
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              {total && (
                <p style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic", textAlign: "center", marginTop: 10 }}>
                  Showing {auditData.beforeAfter.length} of {total} products. MagicFeedPro optimizes your entire catalog automatically.
                </p>
              )}
            </div>

            {/* ══════════════════════════════════════════════════
                ADDITION 4 — PRIORITY ACTION PLAN
               ══════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0D0D1A", marginBottom: 14 }}>Your 3-step fix plan</div>
              <div className="audit-priority-row" style={{ display: "flex", gap: 10 }}>
                {[
                  {
                    num: 1, emoji: "🔴", label: "Priority 1", color: "#EF4444", bg: "#FEE2E2",
                    title: "Fix your product titles",
                    text: `Avg ${auditData.avgTitleLen} chars — needs to reach 70–150 chars with brand, type and key attributes.`,
                    impact: `+${Math.round(impImpressions * 0.5)}% impressions`,
                  },
                  {
                    num: 2, emoji: "🟠", label: "Priority 2", color: "#F59E0B", bg: "#FEF3C7",
                    title: "Rewrite your descriptions",
                    text: `Avg ${auditData.avgDescLen} chars — needs to reach 500+ chars with targeted keywords.`,
                    impact: `+${Math.round(impClicks * 0.6)}% click rate`,
                  },
                  {
                    num: 3, emoji: "⭐", label: "Priority 3", color: "#F59E0B", bg: "#FEF3C7",
                    title: "Configure Product Highlights",
                    text: `Missing on ${auditData.hlMissingPct}% of products — one of the biggest missed opportunities on Shopping.`,
                    impact: `+${Math.round(impImpressions * 0.35)}% visibility`,
                  },
                ].map((card) => (
                  <div key={card.num} style={{
                    flex: 1, background: "#fff", borderRadius: 16, padding: "16px 14px",
                    border: "1.5px solid #E5E7EB", position: "relative",
                    display: "flex", flexDirection: "column",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}>
                    {/* Number badge */}
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", background: card.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: 10,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: card.color }}>{card.num}</span>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: card.color, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {card.emoji} {card.label}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0D0D1A", marginBottom: 6, lineHeight: 1.3 }}>
                      {card.title}
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5, flex: 1, marginBottom: 12 }}>
                      {card.text}
                    </div>
                    <div style={{
                      background: "#D1FAE5", borderRadius: 20, padding: "4px 10px",
                      fontSize: 11, fontWeight: 700, color: "#059669", alignSelf: "flex-start",
                    }}>
                      {card.impact}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── REMAINING PRODUCTS UPSELL ── */}
            {remaining > 0 && (
              <div style={{
                background: "#FFFBEB", borderRadius: 12, padding: "16px 20px",
                border: "1px solid #FDE68A", marginBottom: 24,
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>📦</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 2 }}>
                    {remaining} more products to optimize
                  </div>
                  <div style={{ fontSize: 12, color: "#A16207", lineHeight: 1.5 }}>
                    This free audit covers {analyzed} products. Sign up to MagicFeedPro to optimize your entire catalog of {total} products automatically.
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════
                ADDITION 5 — PERSONALIZED CTA
               ══════════════════════════════════════════════════ */}
            <div className="audit-cta-section" style={{
              background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
              borderRadius: 16, padding: "32px 28px", textAlign: "center",
              border: "1px solid rgba(75,106,232,0.12)",
            }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>🚀</div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0D0D1A", marginBottom: 8, lineHeight: 1.3 }}>
                Ready to fix your feed?
              </h3>
              <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 20, lineHeight: 1.6 }}>
                {total
                  ? `Optimize all ${total} products automatically with AI — titles, descriptions & highlights — and rank higher on Google Shopping.`
                  : "MagicFeedPro automatically optimizes your titles, descriptions & highlights with AI — so you rank higher on Google Shopping."
                }
              </p>
              <a
                href="https://app.magicfeedpro.com/register"
                onClick={() => { try { trackCtaAudit(storeName || ""); } catch {} }}
                style={{
                  display: "inline-block", width: "100%", maxWidth: 360, padding: "14px 32px",
                  background: "linear-gradient(135deg,#4B6AE8,#6B8BF5)",
                  borderRadius: 12, color: "#fff", fontSize: 15, fontWeight: 700, textDecoration: "none",
                  boxShadow: "0 4px 20px rgba(75,106,232,0.4)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  fontFamily: "'Inter',sans-serif", boxSizing: "border-box",
                }}
                className="audit-cta-btn"
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(75,106,232,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(75,106,232,0.4)"; }}
              >
                {total
                  ? `→ Optimize All ${total} Products with AI`
                  : "→ Start Your Free Trial"
                }
              </a>
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 14 }}>
                No contract • Setup in 10 min • 200+ 5-star reviews
              </p>
            </div>

          </div>{/* end main card */}
        </div>{/* end max-w container */}
      </div>
    </>
  );
}
